import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import {
  createDefaultAvailability,
  dayIndex,
  AVAILABILITY_TYPES,
  ADDRESS_TYPES,
} from "../../config/availability";
import { FORM_MODE } from "../../config/constants";
import Input from "../Shared/Input";
import RadioGroup from "../Shared/RadioGroup";
import Badge from "../Shared/Badge";
import WeeklyAvailability from "./WeeklyAvailability";
import { nanoid } from "nanoid";
import FormFooter from "../Shared/FormFooter";
import FormWrapper from "../Shared/FormWrapper";
import AlertRed from "../Shared/AlertRed";
import ErrorMessage from "../Shared/ErrorMessage";
import AddressLookupNative from "../Shared/AddressLookupNative";
import AlertModal from "../Shared/AlertModal";
import Toggle from "../Shared/Toggle";
import {
  useBookingPage,
  useUpdateSettingsTab,
} from "../../hooks/useBookingPage";
import { mapApiError } from "../../utils/formErrorMapper";
import useScrollToFirstError from "../../hooks/useScrollToFirstError";
import { deriveDistanceUnit } from "../../utility/distanceUnitUtils";

const AvailabilityForm = ({
  defaultAvailability,
  closeDrawerHandler,
  saveAvailability,
  updateAvailability,
  patchAvailability,
  mode,
  activeTabGroup,
  onCancelHandler,
  setIsFormDirty,
  // Label for the footer's secondary button. Defaults to "Cancel"; the service
  // form passes "Back to service" when this form is embedded as a step in the
  // service drawer (see ServiceForm's availability step swap).
  secondaryCaption = "Cancel",
  // When true, hides the "Where do clients meet you?" radio and pins the
  // form to `activeTabGroup`'s type — used when the caller opened this form
  // to add a SPECIFIC missing type (e.g. the attention strip's "Add mobile
  // availability" / "Add a location" CTAs) so the user can't accidentally
  // create the wrong delivery type. Default false keeps every existing
  // caller (AvailabilityPage) unchanged.
  lockMeetMode = false,
}) => {
  // Primary "where do clients meet you?" choice. Kept in local state (NOT
  // react-hook-form) so it never leaks into the create/update payload — the
  // submit path forwards the whole RHF `data` object.
  const MEET_MODES = { TRAVEL: "travel", FIXED: "fixed" };

  // Map the active tab group ("mobile" | "fixed") to a concrete availabilityType
  // for new records. The group is NOT a valid availabilityType on its own.
  const groupDefaultType =
    activeTabGroup === "fixed"
      ? AVAILABILITY_TYPES.FIXED
      : AVAILABILITY_TYPES.DISTANCE;

  // Derive the starting availabilityType: an existing record's stored type when
  // editing, otherwise the tab group's default.
  const initialType =
    defaultAvailability?.availabilityType ?? groupDefaultType;

  const [meetMode, setMeetMode] = useState(
    initialType === AVAILABILITY_TYPES.FIXED
      ? MEET_MODES.FIXED
      : MEET_MODES.TRAVEL
  );
  const [selectedAddress, setSelectedAddress] = useState("");
  const [regionErrorMessage, setRegionErrorMessage] = useState(null);
  const [baseAddressErrorMessage, setBaseAddressErrorMessage] = useState(null);
  const [availabilityErrorMessage, setAvailabilityErrorMessage] =
    useState(null);
  const [banner, setBanner] = useState(null);
  const bannerRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastSubmitPayload, setLastSubmitPayload] = useState(null);
  const [availability, setAvailability] = useState(() => {
    if (!defaultAvailability?.weeklyAvailability) {
      return createDefaultAvailability();
    }
    let availability = [];
    for (const day in defaultAvailability.weeklyAvailability) {
      availability.push({
        day: day,
        dayIndex: dayIndex[day],
        blocks: defaultAvailability.weeklyAvailability[day].map(
          (availabilityBlock) => {
            return {
              startTime: { id: nanoid(), name: availabilityBlock.startTime },
              endTime: { id: nanoid(), name: availabilityBlock.endTime },
            };
          }
        ),
        unavailable: defaultAvailability.weeklyAvailability[day].length === 0,
      });
    }
    availability = availability.sort((a, b) => a.dayIndex - b.dayIndex);
    return availability;
  });
  const [selectedRegions, setSelectedRegions] = useState(() => {
    if (!defaultAvailability?.regions) return [];
    return defaultAvailability.regions.map((region) => {
      return {
        id: region.placeId,
        name: region.name,
      };
    });
  });
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isDirty, dirtyFields },
    setError,
  } = useForm({
    defaultValues: {
      name: defaultAvailability?.name ? defaultAvailability.name : "",
      availabilityType: initialType,
      regions: defaultAvailability?.regions ?? [],
      weeklyAvailability: defaultAvailability?.weeklyAvailability,
      baseAddress: defaultAvailability?.baseAddress,
      distance: defaultAvailability?.distance,
      distanceUnit: defaultAvailability?.distanceUnit
        ? defaultAvailability.distanceUnit
        : "km",
    },
  });
  const selectedAvailabilityType = watch("availabilityType");
  const weeklyAvailability = watch("weeklyAvailability");
  const baseAddress = watch("baseAddress");

  // -------------------------------------------------------------------------
  // Hide-address mirror — saves through PATCH /booking-page/settings, not
  // the availability payload. Pattern mirrors the travel-fee block in ServiceForm.
  // -------------------------------------------------------------------------
  const { data: bookingPageData } = useBookingPage();
  const { mutate: updateSettings, isPending: isUpdatingHideAddress } =
    useUpdateSettingsTab();

  const [hideAddressModalOpen, setHideAddressModalOpen] = useState(false);
  const [hideAddressOnModalOpen, setHideAddressOnModalOpen] = useState(false);
  const [hideAddressBanner, setHideAddressBanner] = useState(null);
  const [hideAddressSuccess, setHideAddressSuccess] = useState(false);

  const {
    watch: watchHide,
    setValue: setValueHide,
    reset: resetHide,
    formState: { isDirty: isHideDirty },
  } = useForm({
    defaultValues: { hideFixedAddressUntilConfirmed: false },
  });

  const hideAddress = watchHide("hideFixedAddressUntilConfirmed");

  // Sync from booking page data (mirrors ServiceForm resetFee pattern)
  useEffect(() => {
    if (bookingPageData?.data) {
      resetHide(
        {
          hideFixedAddressUntilConfirmed:
            bookingPageData.data.hideFixedAddressUntilConfirmed ?? false,
        },
        { keepDirty: false }
      );
    }
  }, [bookingPageData, resetHide]);

  const handleHideAddressToggle = (checked) => {
    if (!checked) {
      setHideAddressModalOpen(true);
      return;
    }
    // Turning ON — explain the consequences before dirtying the value
    setHideAddressOnModalOpen(true);
  };

  const confirmTurnOnHideAddress = () => {
    setValueHide("hideFixedAddressUntilConfirmed", true, { shouldDirty: true });
    setHideAddressOnModalOpen(false);
    setHideAddressBanner(null);
    setHideAddressSuccess(false);
  };

  const confirmTurnOffHideAddress = () => {
    setValueHide("hideFixedAddressUntilConfirmed", false, {
      shouldDirty: true,
    });
    setHideAddressModalOpen(false);
    setHideAddressBanner(null);
    setHideAddressSuccess(false);
  };

  // Returns a Promise so runSubmit can await the settings PATCH before the
  // availability-save success path is allowed to close the drawer. Rejecting
  // (rather than swallowing) lets the error flow into runSubmit's existing
  // catch/mapApiError path, which sets the top-of-form `banner` — the same
  // mechanism availability-save errors use — so a settings-save failure keeps
  // the drawer open with a visible error instead of vanishing silently.
  const saveHideAddress = (data) => {
    setHideAddressBanner(null);
    setHideAddressSuccess(false);
    return new Promise((resolve, reject) => {
      updateSettings(data, {
        onSuccess: (result) => {
          setHideAddressSuccess(true);
          setTimeout(() => setHideAddressSuccess(false), 3000);
          resolve(result);
        },
        onError: (err) => {
          const msg =
            err.response?.data?.error?.message ||
            "Failed to save address visibility settings";
          setHideAddressBanner({ type: "error", message: msg });
          reject(err);
        },
      });
    });
  };
  const onSelect = (region) => {
    if (region && region.id) {
      const isRegionSelected = selectedRegions.some(
        (selectedRegion) => selectedRegion.id === region.id
      );
      if (isRegionSelected) return;

      const updatedRegions = [...selectedRegions, region];
      setSelectedRegions(updatedRegions);
      setValue("regions", updatedRegions, {
        shouldDirty: true,
      });
    }
  };
  const removeRegion = (id) => {
    const updatedRegions = selectedRegions.filter((region) => region.id !== id);
    setSelectedRegions(updatedRegions);
    setValue("regions", updatedRegions, {
      shouldDirty: true,
    });
  };
  const formatAvailability = (availability) => {
    let updatedWeeklyAvailability = {};
    availability.forEach((value) => {
      updatedWeeklyAvailability[value.day] = value.unavailable
        ? []
        : value.blocks.map((block) => {
            return {
              startTime: block.startTime.name,
              endTime: block.endTime.name,
            };
          });
    });
    return updatedWeeklyAvailability;
  };
  const isFieldsValid = () => {
    if (
      selectedAvailabilityType === AVAILABILITY_TYPES.REGION &&
      !selectedRegions.length
    ) {
      setRegionErrorMessage("Please select at least one area.");
      return false;
    }
    if (
      selectedAvailabilityType === AVAILABILITY_TYPES.DISTANCE &&
      !baseAddress
    ) {
      setBaseAddressErrorMessage("Please enter your home base address.");
      return false;
    }
    if (selectedAvailabilityType === AVAILABILITY_TYPES.FIXED && !baseAddress) {
      setBaseAddressErrorMessage("Please enter your service location address.");
      return false;
    }

    const isFullyUnavailable = Object.values(weeklyAvailability).every(
      (day) => day.length === 0
    );
    if (isFullyUnavailable) {
      setAvailabilityErrorMessage("Set hours for at least one day.");
      return false;
    }
    return true;
  };
  const runSubmit = async (data) => {
    if (isSubmitting) return;
    const isUncontrolledFieldsValid = isFieldsValid();
    if (!isUncontrolledFieldsValid) return;

    setBanner(null);
    setLastSubmitPayload(data);
    setIsSubmitting(true);
    try {
      // If the hide-address toggle was changed, persist it through updateSettings
      // BEFORE the availability save. The availability mutations' onSuccess
      // handlers (in AvailabilityPage) close the drawer — and unmount this form —
      // as soon as they resolve, so the settings save must complete (and any
      // failure must be surfaced) first, or its result would land on an
      // unmounted component and the user would never see a failure.
      if (isHideDirty) {
        await saveHideAddress({ hideFixedAddressUntilConfirmed: hideAddress });
      }

      if (mode === FORM_MODE.CREATE) {
        await saveAvailability({
          data,
          availabilityType: data.availabilityType,
        });
      } else if (mode === FORM_MODE.UPDATE) {
        const { _id: id } = defaultAvailability;
        if (
          dirtyFields.regions ||
          dirtyFields.baseAddress ||
          dirtyFields.availabilityType
        ) {
          await updateAvailability({
            id,
            data: { ...data },
            availabilityType: data.availabilityType,
          });
        } else {
          const updatedData = {};
          for (const key in dirtyFields) {
            if (dirtyFields[key]) {
              updatedData[key] = data[key];
            }
          }
          await patchAvailability({ id, formData: { ...updatedData } });
        }
      }
    } catch (err) {
      // baseAddress/regions/weeklyAvailability are uncontrolled (not
      // react-hook-form-registered) — setError would no-op for them, so route
      // those three to their existing local-state setters + <ErrorMessage>
      // instead. Everything else (name, distance, distanceUnit,
      // availabilityType) falls through to RHF's setError as before.
      const setFieldError = (field, message) => {
        if (field === "baseAddress") {
          setBaseAddressErrorMessage(message);
          return true;
        }
        if (field === "regions") {
          setRegionErrorMessage(message);
          return true;
        }
        if (field === "weeklyAvailability") {
          setAvailabilityErrorMessage(message);
          return true;
        }
        return false;
      };
      const result = mapApiError(err, { setError, setFieldError });
      if (result.handled) return;
      if (result.kind === "auth") return;
      setBanner(result.banner);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRetry = () => {
    if (lastSubmitPayload) runSubmit(lastSubmitPayload);
  };

  // Primary choice — how clients and provider meet.
  const meetModeOptions = [
    {
      id: MEET_MODES.TRAVEL,
      title: "I travel to clients",
      description: "You go to the client's location.",
    },
    {
      id: MEET_MODES.FIXED,
      title: "Clients come to me",
      description: "Add your studio, office, or workspace address.",
    },
  ];
  // Secondary choice (travel only) — how the coverage area is defined.
  const coverageOptions = [
    {
      id: AVAILABILITY_TYPES.DISTANCE,
      title: "Within a set distance of a base address",
      description: "Set a home base and a maximum travel distance.",
    },
    {
      id: AVAILABILITY_TYPES.REGION,
      title: "Specific areas or neighborhoods",
      description: "Choose the areas you cover.",
    },
  ];
  const distanceUnitOptions = [
    { id: "km", title: "km" },
    { id: "mi", title: "mi" },
  ];
  useEffect(() => {
    if (selectedAddress?.name) {
      setValue("distanceUnit", deriveDistanceUnit(selectedAddress.name));
      setValue("baseAddress", {
        address: selectedAddress.name,
        placeId: selectedAddress.id,
      });
    }
    setBaseAddressErrorMessage(null);
  }, [selectedAddress, setValue]);
  useEffect(() => {
    const updatedWeeklyAvailability = formatAvailability(availability);
    setValue("weeklyAvailability", updatedWeeklyAvailability, {
      shouldDirty: true,
    });
    setAvailabilityErrorMessage(null);
  }, [availability, setValue]);
  useEffect(() => {
    const selectedRegionsFormatted = selectedRegions.map((region) => {
      return {
        name: region.name,
        placeId: region.id,
      };
    });
    setValue("regions", selectedRegionsFormatted);
    setRegionErrorMessage(null);
  }, [selectedRegions, setValue]);
  useEffect(() => {
    setIsFormDirty(isDirty);
  }, [isDirty, setIsFormDirty]);

  // Keep availabilityType in lockstep with the primary meet-mode choice.
  // meetMode === "fixed"  → availabilityType is always "fixed".
  // meetMode === "travel" → availabilityType is a coverage type (distance/region);
  //                         default to "distance" when switching in from fixed.
  useEffect(() => {
    if (meetMode === MEET_MODES.FIXED) {
      if (selectedAvailabilityType !== AVAILABILITY_TYPES.FIXED) {
        setValue("availabilityType", AVAILABILITY_TYPES.FIXED, {
          shouldDirty: true,
        });
      }
    } else if (
      selectedAvailabilityType !== AVAILABILITY_TYPES.DISTANCE &&
      selectedAvailabilityType !== AVAILABILITY_TYPES.REGION
    ) {
      setValue("availabilityType", AVAILABILITY_TYPES.DISTANCE, {
        shouldDirty: true,
      });
    }
  }, [meetMode, selectedAvailabilityType, setValue, MEET_MODES.FIXED]);

  useScrollToFirstError(errors, {
    regions: regionErrorMessage,
    baseAddress: baseAddressErrorMessage,
    weeklyAvailability: availabilityErrorMessage,
  });

  // The submit-failure banner renders at the top of the form, but this form
  // is tall (weekly grid, regions, address) — nothing else scrolls the
  // banner into view, so it can appear off-screen. Mirrors
  // useScrollToFirstError's scroll-into-view pattern (smooth, centered) and
  // the identical fix already shipped in ServiceForm.
  useEffect(() => {
    if (!banner) return;
    bannerRef.current?.scrollIntoView?.({ behavior: "smooth", block: "center" });
  }, [banner]);

  return (
    <>
    <FormWrapper onSubmit={handleSubmit(runSubmit)}>
      {banner ? (
        <div className="mb-4" ref={bannerRef}>
          <AlertRed
            title={banner.title}
            message={banner.message}
            onRetry={handleRetry}
          />
        </div>
      ) : null}
      <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
        <div className="col-span-full">
          <Input
            id="name"
            name="name"
            label="Name"
            placeholder="e.g., Downtown service area"
            errormessage={errors?.name?.message}
            {...register("name", {
              onBlur: (e) => {
                setValue("name", e.target.value.trim());
              },
              required: "Availability name is required",
            })}
          />
        </div>
        <div className="col-span-full">
          {lockMeetMode ? (
            <p className="text-sm font-medium text-ink-900">
              {meetMode === MEET_MODES.FIXED
                ? "Location — clients come to you"
                : "Mobile availability — you travel to clients"}
            </p>
          ) : (
            <RadioGroup
              label="Where do clients meet you?"
              name="meetMode"
              options={meetModeOptions}
              value={meetMode}
              onChange={setMeetMode}
              layout="vertical"
            />
          )}
          {/* availabilityType errors are surfaced here — at the always-rendered
              meet-mode block — because the coverage radio below only mounts in
              TRAVEL mode. A server error attributed to availabilityType (e.g.
              the "Please choose how clients meet you." 400) would otherwise
              render nowhere and be silently swallowed. */}
          {errors.availabilityType && (
            <ErrorMessage message={errors.availabilityType.message} />
          )}
        </div>
        {meetMode === MEET_MODES.TRAVEL ? (
          <div className="col-span-full">
            <RadioGroup
              label="How do you define where you travel?"
              name="availabilityType"
              options={coverageOptions}
              register={register}
              required="This field is required"
              layout="vertical"
            />
          </div>
        ) : null}
        {selectedAvailabilityType === AVAILABILITY_TYPES.REGION ? (
          <div className="sm:col-span-6" id="regions">
            <AddressLookupNative
              key={"availabilityRegion"}
              label={"Availability region"}
              onSelect={onSelect}
              placeholder={"Search for a region..."}
              isError={regionErrorMessage}
              addressType={ADDRESS_TYPES.REGION}
            />
            <p className="text-sm text-ink-500 mt-0.5">
              All areas must be in the same timezone.
            </p>
            {regionErrorMessage ? (
              <ErrorMessage message={regionErrorMessage} />
            ) : null}
            {selectedRegions.length > 0 ? <div className="mt-4" /> : null}
            <div className="flex flex-wrap gap-2">
              {selectedRegions.map((region) => (
                <Badge
                  key={region.id}
                  badgeName={region.name}
                  onRemove={() => removeRegion(region.id)}
                />
              ))}
            </div>
          </div>
        ) : null}
        {selectedAvailabilityType === AVAILABILITY_TYPES.DISTANCE ||
        selectedAvailabilityType === AVAILABILITY_TYPES.FIXED ? (
          <div className="sm:col-span-6" id="baseAddress">
            <AddressLookupNative
              key={"baseAddress"}
              label={
                selectedAvailabilityType === AVAILABILITY_TYPES.FIXED
                  ? "Service address"
                  : "Base address"
              }
              onSelect={(address) => {
                setSelectedAddress(address);
                setValue("baseAddress", address, {
                  shouldDirty: true,
                });
              }}
              placeholder={
                selectedAvailabilityType === AVAILABILITY_TYPES.DISTANCE
                  ? "Search for your base location..."
                  : "Find your service location..."
              }
              isError={baseAddressErrorMessage}
              addressType={ADDRESS_TYPES.ADDRESS}
              defaultValue={
                defaultAvailability?.baseAddress
                  ? {
                      id: defaultAvailability.baseAddress.placeId,
                      name: defaultAvailability.baseAddress.address,
                    }
                  : null
              }
            />
            {baseAddressErrorMessage ? (
              <ErrorMessage message={baseAddressErrorMessage} />
            ) : null}
            {selectedAvailabilityType === AVAILABILITY_TYPES.DISTANCE ? (
              <div className="mt-4 flex items-center gap-x-6">
                <div className="flex-1">
                  <Input
                    id="distance"
                    name="distance"
                    type="number"
                    label="Maximum travel distance"
                    placeholder="e.g., 25"
                    errormessage={errors?.distance?.message}
                    {...register("distance", {
                      required: "Maximum travel distance is required",
                      valueAsNumber: true,
                      validate: (value) =>
                        value > 0 || "Distance must be greater than 0",
                    })}
                  />
                </div>
                <div className="flex items-center gap-x-4 pt-3">
                  <div>
                    <RadioGroup
                      name="distanceUnit"
                      options={distanceUnitOptions}
                      register={register}
                      required="This field is required"
                    />
                    {errors.distanceUnit && (
                      <ErrorMessage message={errors.distanceUnit.message} />
                    )}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
        {selectedAvailabilityType === AVAILABILITY_TYPES.FIXED ? (
          <div className="col-span-full rounded-card border border-ink-200 bg-cream-100 p-4">
            <div>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-ink-900">
                    Hide your location's address until you approve
                  </h3>
                  <p className="text-sm text-ink-500 mt-0.5">
                    Clients see your location's name, not the address, until you
                    approve their booking. Applies to all your locations.
                  </p>
                </div>
                <Toggle
                  checked={hideAddress}
                  onChange={handleHideAddressToggle}
                />
              </div>
              {hideAddressBanner?.type === "error" && (
                <div className="mt-3">
                  <AlertRed
                    title="Error"
                    message={hideAddressBanner.message}
                  />
                </div>
              )}
              {hideAddressSuccess && (
                <p className="mt-2 text-sm text-signal-green-600">
                  Address visibility updated.
                </p>
              )}
            </div>
          </div>
        ) : null}
        <div className="col-span-full" id="weeklyAvailability">
          <WeeklyAvailability
            availability={availability}
            setAvailability={setAvailability}
          />
          {availabilityErrorMessage ? (
            <ErrorMessage message={availabilityErrorMessage} />
          ) : null}
        </div>
      </div>
      <FormFooter
        primaryCaption={
          mode === FORM_MODE.CREATE ? "Create availability" : "Save changes"
        }
        secondaryCaption={secondaryCaption}
        secondaryClickHandler={onCancelHandler}
        isLoading={isSubmitting}
        requiresVerification={false}
      />
    </FormWrapper>

    <AlertModal
      isOpen={hideAddressOnModalOpen}
      modalTitle="Turn on address privacy?"
      modalMessage={
        <ul className="list-disc space-y-2 pl-5">
          <li>
            Clients see your location's name, not the address, until you approve
            their booking.
          </li>
          <li>
            Every booking will wait for your approval before it's confirmed.
          </li>
          <li>Auto-approval turns off while this is on.</li>
          <li>
            Your location name is shown publicly — don't put the street address
            there.
          </li>
        </ul>
      }
      primaryCaption="Turn on"
      secondaryCaption="Cancel"
      primaryClickHandler={confirmTurnOnHideAddress}
      dismissModalHandler={() => setHideAddressOnModalOpen(false)}
      primaryButtonColour="blue"
      showIcon={false}
    />

    <AlertModal
      isOpen={hideAddressModalOpen}
      modalTitle="Show your address?"
      modalMessage="Turning this off makes your location's address visible on your public booking page."
      primaryCaption="Show address"
      secondaryCaption="Keep hidden"
      primaryClickHandler={confirmTurnOffHideAddress}
      dismissModalHandler={() => setHideAddressModalOpen(false)}
      primaryButtonColour="red"
    />
    </>
  );
};

export default AvailabilityForm;
